import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import dayjs from "dayjs";
import { ArchiveRestoreIcon } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { getListProjectsQueryKey } from "~/api/folders/folders";
import { Folder } from "~/api/model";
import { useRestoreProject } from "~/api/projects/projects";
import { Button } from "~/components/ui/button";
import { TableCell, TableRow } from "~/components/ui/table";
import { UserAvatar } from "~/components/ui/user-avatar";
import { useOrganizationParams } from "~/helpers";
import { RestoreCaseDialog } from "./RestoreCaseDialog";

type ArchivedCasesListItemProps = {
  folder: Folder;
};

export const ArchivedCasesListItem: React.FC<ArchivedCasesListItemProps> = ({
  folder,
}) => {
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: restoreProject } = useRestoreProject();
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);

  const queryClient = useQueryClient();

  const onCaseRestored = async () => {
    await restoreProject({
      organizationId,
      projectId: folder.id,
    });

    await queryClient.invalidateQueries({
      queryKey: getListProjectsQueryKey(organizationId, { archived: true }),
    });

    await queryClient.invalidateQueries({
      queryKey: getListProjectsQueryKey(organizationId),
    });

    setShowRestoreDialog(false);
  };

  return (
    <>
      <TableRow
        key={folder.id}
        className={clsx(
          "text-zinc-500 !text-sm !font-medium leading-tight w-full grid grid-cols-12 gap-4 group",
          "hover:bg-gray-100"
        )}
        data-folder-id={folder.id}
      >
        <TableCell className="py-4 font-medium col-span-4 flex items-center">
          <span className="text-zinc-900 text-sm font-medium leading-tight">
            {folder.name}
          </span>
        </TableCell>
        <TableCell className="py-4 col-span-2 flex items-center">
          <span className="text-zinc-500 text-sm font-normal leading-tight">
            {folder.fileCount}
          </span>
        </TableCell>
        <TableCell className="py-4 col-span-2 flex items-center">
          <span className="text-zinc-500 text-sm font-normal leading-tight">
            {dayjs(folder.archivedAt).format("DD/MM/YYYY, HH:mm")}
          </span>
        </TableCell>
        <TableCell className="py-4 col-span-2 flex items-center">
          {folder.archivedBy && (
            <div className="flex items-center gap-2">
              <UserAvatar name={folder.archivedBy.name} size="sm" />
              <span className="text-sm font-medium">
                {folder.archivedBy.name}
              </span>
            </div>
          )}
        </TableCell>
        <TableCell className="py-4 flex items-center justify-end col-span-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRestoreDialog(true)}
            className="hover:bg-transparent opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ArchiveRestoreIcon className="w-4 h-4 mr-2" />
            <FormattedMessage id="restore" defaultMessage="Restore" />
          </Button>
        </TableCell>
      </TableRow>
      {showRestoreDialog && (
        <RestoreCaseDialog
          open={showRestoreDialog}
          setOpen={setShowRestoreDialog}
          folder={folder}
          onCaseRestored={onCaseRestored}
        />
      )}
    </>
  );
};
